import { sha256 } from "js-sha256";
import backend from "@/api";
import { handleError } from "@/services/handleErrors";

const SCRIPT_ID = "sberoprosWidget";
const SCRIPT_URL = "https://sberopros.sber.ru/widget/js/sberoprosWidget.js";
const SURVEY_URL = "https://sberopros.sber.ru/u/PLwqlx";
const SCRIPT_LOAD_TIMEOUT = 15000;
const SURVEY_TIMEOUT_DAYS = 90;
const SURVEY_HOOKS = ["onSberoprosSurveyDeclined", "onSberoprosSurveyCompleted"];

let scriptLoading;

const loadSberoprosScript = () => {
  if (typeof window.initSberoprosWidget === "function") return Promise.resolve();
  if (scriptLoading) return scriptLoading;

  scriptLoading = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(SCRIPT_ID);
    const script = existingScript || document.createElement("script");

    const finish = error => {
      clearTimeout(timeout);
      script.removeEventListener("load", onLoad);
      script.removeEventListener("error", onError);

      if (error) {
        script.remove();
        reject(error);
      } else {
        resolve();
      }
    };

    const onLoad = () => {
      finish(
        typeof window.initSberoprosWidget === "function"
          ? null
          : new Error("SberOpros initialization API is unavailable"),
      );
    };
    const onError = () => finish(new Error("Failed to load SberOpros widget script"));
    const timeout = setTimeout(
      () => finish(new Error("SberOpros widget script loading timed out")),
      SCRIPT_LOAD_TIMEOUT,
    );

    script.addEventListener("load", onLoad);
    script.addEventListener("error", onError);

    if (!existingScript) {
      script.id = SCRIPT_ID;
      script.async = true;
      script.src = SCRIPT_URL;
      document.body.appendChild(script);
    }
  }).catch(error => {
    scriptLoading = null;
    throw error;
  });

  return scriptLoading;
};

const removeSberoprosWidget = () => {
  try {
    window.removeSberoprosWidget?.();
  } catch (error) {
    console.warn("Failed to remove SberOpros widget", error);
  }
};

const getSurveyCid = state => {
  const { current_user, auth_token } = state.account;

  if (!auth_token || !current_user) return null;

  const { id, inn } = current_user;
  if (id == null || typeof inn !== "string" || !inn.trim()) return null;

  return `${sha256(inn)}_${id}`;
};

const getSurveyState = state => {
  const cid = getSurveyCid(state);
  return cid == null ? null : `${cid}:${state.account.current_user.canPassSberOpros === true}`;
};

export const initSberoprosWidget = store => {
  let revision = 0;
  let disposed = false;
  let finishedCid = null;
  let onSurveyFinished = null;
  const previousHooks = SURVEY_HOOKS.map(hook => window[hook]);

  const clearSurveyHooks = () => {
    if (!onSurveyFinished) return;

    SURVEY_HOOKS.forEach((hook, index) => {
      if (window[hook] === onSurveyFinished) window[hook] = previousHooks[index];
    });
    onSurveyFinished = null;
  };

  const unwatch = store.watch(
    getSurveyState,
    async surveyState => {
      const cid = getSurveyCid(store.state);
      const canPass = store.state.account.current_user?.canPassSberOpros === true;

      // Таймаут запрещает следующий запуск, но не закрывает текущий опрос и благодарность.
      if (!canPass && cid != null && cid === finishedCid) return;

      const currentRevision = ++revision;
      finishedCid = null;
      clearSurveyHooks();
      removeSberoprosWidget();

      if (cid == null || !canPass) return;

      try {
        await loadSberoprosScript();

        // Право на опрос или данные пользователя могли измениться во время загрузки.
        if (currentRevision !== revision || getSurveyState(store.state) !== surveyState) return;

        let timeoutRequested = false;
        onSurveyFinished = async () => {
          if (timeoutRequested || currentRevision !== revision || getSurveyState(store.state) !== surveyState) return;

          timeoutRequested = true;
          finishedCid = cid;
          const { current_user, auth_token } = store.state.account;
          const userId = current_user.id;
          const isCurrentSession = () => {
            const account = store.state.account;
            return !disposed && account.current_user?.id === userId && account.auth_token === auth_token;
          };

          try {
            const { data } = await backend.post(store.state.paths.sberopros_set_timeout, { days: SURVEY_TIMEOUT_DAYS });

            if (isCurrentSession()) store.commit("updateSberoprosState", data);
          } catch (error) {
            timeoutRequested = false;
            if (isCurrentSession()) {
              await handleError(error);
            } else {
              console.warn("Failed to set SberOpros survey timeout", error);
            }
          }
        };

        SURVEY_HOOKS.forEach(hook => {
          window[hook] = onSurveyFinished;
        });

        window.initSberoprosWidget(`${SURVEY_URL}?cid=${encodeURIComponent(cid)}`);
      } catch (error) {
        if (currentRevision === revision) clearSurveyHooks();
        console.warn("Failed to initialize SberOpros widget", error);
      }
    },
    { immediate: true },
  );

  return () => {
    disposed = true;
    revision += 1;
    finishedCid = null;
    unwatch();
    clearSurveyHooks();
    removeSberoprosWidget();
  };
};
